import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Activity, ArrowLeft, FileText, Scale } from 'lucide-react';
import apiService from '../../services/apiService';
import ForecastStatementMatrix from './ForecastStatementMatrix';
import { resolveCycle } from './forecastCycle';
import { getActiveForecastUser } from './forecastUsers';

const GRAINS = [
  { key: 'default', label: 'Recent' },
  { key: 'fy', label: 'Fiscal year' },
  { key: 'quarter', label: 'Quarter' },
  { key: 'month', label: 'Month' }
];

// Gateway errors (e.g. Vercel 504s) return objects — never render those raw.
const errText = (err) => {
  const detail = err?.response?.data?.error ?? err?.response?.data ?? err?.message ?? err;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const ForecastStatements = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Active cycle: shared constant, overridable per-view via ?cycle= — not hardcoded.
  const cycle = useMemo(() => resolveCycle(searchParams.get('cycle')), [searchParams]);
  const [submissions, setSubmissions] = useState([]);
  const [level, setLevel] = useState(searchParams.get('level') || 'entity');
  const [entity, setEntity] = useState(searchParams.get('entity') || '');
  const [division, setDivision] = useState(searchParams.get('division') || '');
  const [market, setMarket] = useState(searchParams.get('market') || '');
  const [basis, setBasis] = useState(searchParams.get('basis') === 'actual' ? 'actual' : 'prior'); // 'prior' | 'actual'
  const [grain, setGrain] = useState(searchParams.get('grain') || 'default');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Roster of planning entities (for the scope picker) from the cycle's submissions.
  useEffect(() => {
    apiService.fetchForecastSubmissions(cycle)
      .then((result) => setSubmissions(result?.submissions || []))
      .catch(() => setSubmissions([]));
  }, [cycle]);

  // Default entity = first unit in the active user's scope (their managed span /
  // assigned units), falling back to the first available submission. Sourced from
  // the user's scope instead of a hardcoded fixture.
  const scopedDefaultEntity = useMemo(() => {
    const user = getActiveForecastUser();
    const mine = submissions.filter(
      (s) => s.fpaManager === user.name || (s.liaison || s.assignedTo) === user.name
    );
    const pool = mine.length ? mine : submissions;
    return pool.find((s) => s.planningEntity)?.planningEntity || '';
  }, [submissions]);

  useEffect(() => {
    if (level === 'entity' && !entity && scopedDefaultEntity) setEntity(scopedDefaultEntity);
  }, [level, entity, scopedDefaultEntity]);

  const divisions = useMemo(
    () => [...new Set(submissions.map((s) => s.division).filter(Boolean))].sort(),
    [submissions]
  );
  const markets = useMemo(
    () => [...new Set(submissions
      .filter((s) => !division || s.division === division)
      .map((s) => s.market).filter(Boolean))].sort(),
    [submissions, division]
  );
  const entityOptions = useMemo(
    () => submissions
      .filter((s) => s.planningEntity)
      .map((s) => ({ value: s.planningEntity, label: `${s.division} · ${s.market}` }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [submissions]
  );

  const activeEntityLabel = useMemo(() => {
    if (level === 'company') return 'Total Company';
    if (level === 'division') return division || 'Pick a division';
    if (level === 'market') return market || 'Pick a market';
    return entityOptions.find((o) => o.value === entity)?.label || entity;
  }, [level, division, market, entity, entityOptions]);

  // Build the comparison query for the active scope.
  const queryParams = useMemo(() => {
    const params = { cycleCode: cycle, grain: 'monthly', detail: 'glGroup', level };
    if (level === 'entity') params.entity = entity;
    if (level === 'division') params.division = division;
    if (level === 'market') params.market = market;
    return params;
  }, [cycle, level, entity, division, market]);

  // Keep the URL in sync so the view is shareable / deep-linkable.
  useEffect(() => {
    const next = { level };
    if (level === 'entity') next.entity = entity;
    if (level === 'division') next.division = division;
    if (level === 'market') next.market = market;
    next.basis = basis;
    next.grain = grain;
    setSearchParams(next, { replace: true });
  }, [level, entity, division, market, basis, grain, setSearchParams]);

  useEffect(() => {
    // Scope not yet resolved — don't fetch (an empty entity/division/market would
    // pull every key and merge unrelated lines into one matrix).
    if (level === 'entity' && !entity) { setData({ groups: [] }); return undefined; }
    if (level === 'division' && !division) { setData({ groups: [] }); return undefined; }
    if (level === 'market' && !market) { setData({ groups: [] }); return undefined; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService.fetchForecastComparison(queryParams)
      .then((result) => { if (!cancelled) setData(result); })
      .catch((err) => { if (!cancelled) setError(errText(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // queryParams already encodes level/entity/division/market; listed members are
    // its inputs, kept explicit only for the pre-fetch scope guard above.
  }, [queryParams, level, entity, division, market]);

  const groups = data?.groups || [];
  const versions = data?.versions || {};
  const cycleName = data?.cycle?.name || 'FY2027 Budget';
  const priorLabel = versions.prior || 'prior forecast';
  const budgetLabel = versions.budget || 'budget';

  return (
    <div className="forecast-module forecast-statements">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            FP&amp;A review / Review Template
            <Link to="/forecasting/review" className="forecast-kicker-back">
              <ArrowLeft size={12} /> Review console
            </Link>
          </div>
          <h1>Statement review — {activeEntityLabel}</h1>
          <p>
            {cycleName} · full Income Statement and Balance Sheet at GL line-item grain — actual
            history through the forecast horizon, reframed against the prior forecast or the actual
            trend.
          </p>
        </div>
        <div className="forecast-command-grid review-filters">
          <label>
            Scope
            <select value={level} onChange={(e) => setLevel(e.target.value)}>
              <option value="entity">Planning entity</option>
              <option value="market">Market</option>
              <option value="division">Division</option>
              <option value="company">Total company</option>
            </select>
          </label>
          {level === 'entity' && (
            <label>
              Entity
              <select value={entity} onChange={(e) => setEntity(e.target.value)}>
                {entityOptions.length === 0 && <option value={entity}>{entity}</option>}
                {entityOptions.map((o) => (
                  <option value={o.value} key={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          )}
          {level === 'division' && (
            <label>
              Division
              <select value={division} onChange={(e) => setDivision(e.target.value)}>
                <option value="">Pick a division…</option>
                {divisions.map((d) => <option value={d} key={d}>{d}</option>)}
              </select>
            </label>
          )}
          {level === 'market' && (
            <label>
              Market
              <select value={market} onChange={(e) => setMarket(e.target.value)}>
                <option value="">Pick a market…</option>
                {markets.map((m) => <option value={m} key={m}>{m}</option>)}
              </select>
            </label>
          )}
        </div>
      </section>

      <section className="forecast-operating-bar">
        <div className="forecast-health-strip">
          <span className="forecast-segmented basis-tabs">
            <button type="button" className={basis === 'prior' ? 'active' : ''} onClick={() => setBasis('prior')}>
              vs Prior Forecast
            </button>
            <button type="button" className={basis === 'actual' ? 'active' : ''} onClick={() => setBasis('actual')}>
              vs Actual Trend
            </button>
          </span>
          <span className="forecast-segmented grain-tabs">
            {GRAINS.map((g) => (
              <button type="button" key={g.key} className={grain === g.key ? 'active' : ''} onClick={() => setGrain(g.key)}>
                {g.label}
              </button>
            ))}
          </span>
          <span className="forecast-health-chip neutral">
            {loading ? 'Loading…' : `${groups.length} GL lines`}
          </span>
        </div>
        <p className="statement-basis-note">
          {basis === 'prior'
            ? <>Δ compares the statement value against the prior cycle forecast (<strong>{priorLabel}</strong>). Where actuals undershoot or beat that plan, the variance lights up.</>
            : <>Δ compares the forecast against the annualized actual run-rate (trailing-12-month actuals). History columns carry no Δ — they are the trend.</>}
        </p>
      </section>

      {error && (
        <div className="forecast-panel forecast-home-error"><p>{error}</p></div>
      )}

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code"><FileText size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />RT_01 ● Income Statement</span>
          <h2>Income statement — GL line-item, actual → forecast</h2>
        </div>
        {loading ? (
          <p className="forecast-submit-note">Loading comparison…</p>
        ) : (
          <ForecastStatementMatrix groups={groups} statement="IS" basis={basis} grain={grain} />
        )}
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code"><Scale size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />RT_02 ● Balance Sheet</span>
          <h2>Balance sheet — end-of-period balances, never summed across months</h2>
        </div>
        {loading ? (
          <p className="forecast-submit-note">Loading comparison…</p>
        ) : (
          <ForecastStatementMatrix groups={groups} statement="BS" basis={basis} grain={grain} />
        )}
      </section>

      <section className="forecast-panel statement-legend-panel">
        <div className="statement-legend">
          <span><i className="src-swatch actual" /> Actual</span>
          <span><i className="src-swatch forecast" /> Forecast · {budgetLabel}</span>
          <span><i className="src-swatch priorForecast" /> Prior-based forecast · {priorLabel}</span>
        </div>
        <p className="forecast-submit-note">
          Figures in $MM. Time runs across the columns — click a fiscal year to drill to quarters, a
          quarter to drill to months, the <strong>FY Σ</strong> column to collapse. Aggregation is
          measure-aware and mirrors the off-trend watcher: <strong>income-statement flows sum</strong>
          {' '}across the span; <strong>balance-sheet lines are point-in-time (EOP)</strong> and show the
          last month of the span — never summed. The spine is Actual where history exists, then the
          current-cycle budget over the forecast horizon, then the prior cycle for years beyond the
          budget. Net income, pre-provision net revenue and net interest income are computed from the
          signed GL lines, not stored.
        </p>
      </section>
    </div>
  );
};

export default ForecastStatements;
